import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";

export default function HiringProcess() {
  const [activeStep, setActiveStep] = useState(-1);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setActiveStep(0);
          observer.disconnect(); // Runs animation sequence only once on viewport entry
        }
      },
      { threshold: 0.15 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (activeStep >= 0 && activeStep < 3) {
      const timer = setTimeout(() => {
        setActiveStep((prev) => prev + 1);
      }, 2500); // Progresses slowly from step 1 to step 4 (slower speed)
      return () => clearTimeout(timer);
    }
  }, [activeStep]);

  const steps = [
    {
      title: "Share Requirements",
      description:
        "Tell us your tech stack, experience levels, and project timeline to define the ideal profile.",
    },
    {
      title: "Receive Pre-Vetted Profiles",
      description:
        "Get matching profiles of pre-screened technology specialists",
    },
    {
      title: "Interview Candidates",
      description:
        "Assess and select the best talent through our seamless, structured interview process.",
    },
    {
      title: "Onboard Talent",
      description:
        "Integrate your chosen professionals into your team with our full onboarding support.",
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="w-full py-16 md:py-20 lg:py-24"
      style={{ backgroundColor: "#001534" }}
    >
      {/* Full width container with minimal side padding */}
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Heading Section */}
        <div className="mb-16 text-center max-w-4xl mx-auto">
          <h2
            className="text-4xl md:text-5xl font-semibold leading-tight mb-5"
            style={{
              fontFamily: "Outfit, sans-serif",
              color: "#ffffff",
            }}
          >
            Hire in 4 Simple Steps
          </h2>

          <p
            className="text-lg md:text-xl leading-relaxed text-center max-w-3xl mx-auto"
            style={{
              fontFamily: '"Open Sans", sans-serif',
              color: "#ffffff",
              opacity: 0.8,
              hyphens:'none'
            }}
          >
            Our proven recruitment process ensures you receive qualified,
            pre-vetted technology professionals quickly and efficiently.
          </p>
        </div>

        {/* Steps Grid - Full width with minimal gaps */}
        <div className="relative w-full">
          {/* Horizontal connecting line - spans full width */}
          <div
            className="absolute top-8 left-0 right-0 h-[2px] hidden lg:block"
            style={{
              background: "linear-gradient(90deg, transparent, #ffffff40, #ffffff60, #ffffff40, transparent)",
            }}
          >
            {/* Active white progress line overlay that animates on the background line */}
            <motion.div
              className="absolute top-0 bottom-0 left-0 bg-white shadow-[0_0_8px_rgba(255,255,255,0.8)]"
              initial={{ left: "12.5%", right: "87.5%" }}
              animate={{ 
                right: activeStep <= 0 
                  ? "87.5%" 
                  : activeStep === 1 
                  ? "62.5%" 
                  : activeStep === 2 
                  ? "37.5%" 
                  : "12.5%"
              }}
              transition={{ duration: 0.4, ease: "easeInOut" }} // Slower horizontal crawling
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-4 xl:gap-6 relative z-10 w-full">
            {steps.map((step, index) => (
              <div key={index} className="text-center px-2">
                {/* Step number circle */}
                <div className="relative flex justify-center mb-5">
                  <motion.div
                    animate={{
                      backgroundColor: index <= activeStep ? "#ffffff" : "#ffffff",
                      color: index <= activeStep ? "#001534" : "#ffffff",
                      borderColor: index <= activeStep ? "#ffffff" : "rgba(255,255,255,0.3)",
                      boxShadow: index === activeStep 
                        ? "0 0 25px rgba(255, 255, 255, 0.75)" 
                        : "0 4px 10px rgba(0,0,0,0.15)"
                    }}
                    transition={{ 
                      duration: 0.5,
                      delay: index === activeStep && index > 0 ? 1.1 : 0 // Delayed circle highlight to match the slower line animation
                    }}
                    whileHover={{
                      scale: 1.15,
                    }}
                    className="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold shadow-lg cursor-pointer border-[3px]"
                    style={{
                      borderStyle: "solid",
                    }}
                  >
                    {index + 1}
                  </motion.div>
                </div>

                {/* Step title */}
                <motion.h4
                  animate={{
                    opacity: index <= activeStep ? 1 : 0.45,
                    scale: index <= activeStep ? 1.02 : 1,
                  }}
                  transition={{ 
                    duration: 0.1,
                    delay: index === activeStep && index > 0 ? 1.1 : 0 // Delayed text highlight to match the slower line animation
                  }}
                  className="text-lg font-semibold mb-2"
                  style={{
                    fontFamily: "Outfit, sans-serif",
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                >
                  {step.title}
                </motion.h4>

                {/* Step description */}
                <motion.p
                  animate={{
                    opacity: index <= activeStep ? 0.85 : 0.35,
                  }}
                  transition={{ 
                    duration: 0.4,
                    delay: index === activeStep && index > 0 ? 1.1 : 0 // Delayed description highlight to match the slower line animation
                  }}
                  className="text-sm leading-relaxed max-w-[260px] mx-auto"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                    fontSize: "14px",
                    lineHeight: "1.5",
                    fontWeight: 400,
                    color: "#ffffff",
                    hyphens: "none"
                  }}
                >
                  {step.description}
                </motion.p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Button */}
        <div className="text-center mt-12">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all duration-300 hover:gap-3"
            style={{
              backgroundColor: "#ffffff",
              color: "#001233",
              fontFamily: "Outfit, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              textDecoration: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "none";
            }}
          >
            Start Hiring <ArrowRight size={16} />
          </a>
        </div>
      </div>
    </section>
  );
}